import { t } from "../lib/i18n";
import React, { useState, useEffect } from 'react'; // Added useEffect
import { useParams, Link } from 'react-router-dom'; // Added useParams, Link
import { getPredictiveTrends, getProperty, savePredictiveTrendsResult, getSavedPredictiveTrendsResult } from '../api'; // Import the new API function
import Tooltip from './Tooltip';
import { showConfirm, showAlert } from '../utils/ModalService';


const PredictiveTrends: React.FC = () => {
    const { propertyId: urlPropertyId } = useParams<{ propertyId: string }>(); // Get propertyId from URL
    const [propertyId, setPropertyId] = useState(urlPropertyId || ''); // Initialize with URL ID
    const [propertyData, setPropertyData] = useState<any>(null);
    const [targetDate, setTargetDate] = useState('');
    const [trends, setTrends] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [savedTrends, setSavedTrends] = useState<any>(null); // New state for saved trends

    // Function to fetch trends
    const fetchTrendsData = async (propId: string, date: string, propData: any) => {
        setLoading(true);
        setError(null);
        try {
            const response = await getPredictiveTrends(propId, date, propData); // Pass propData
            console.log("Predictive Trends API Response:", response);
            setTrends(response);
        } catch (err: any) { // Explicitly type err as 'any' to access response property
            if (err.response && err.response.data && err.response.data.detail) {
                const detail = err.response.data.detail;
                setError(typeof detail === 'object' ? JSON.stringify(detail) : detail);
            } else {
                setError('An unexpected error occurred while fetching predictive trends.');
            }
            console.error(err);
        }
        setLoading(false);
    };

    const handleSaveTrends = async () => {
        if (!trends || !propertyId || !targetDate || !propertyData) return;
        try {
            const inputParameters = { propertyId: propertyId, targetDate: targetDate, propertyData: propertyData }; // Define input parameters
            await savePredictiveTrendsResult({ property_id: propertyId, input_parameters: inputParameters, generated_result: trends });
            setSavedTrends(trends); // Update savedTrends state
            showAlert('Predictive trends saved successfully!');
        } catch (err) {
            console.error('Error saving predictive trends:', err);
            showAlert('Failed to save predictive trends.');
        }
    };

    useEffect(() => {
        if (urlPropertyId) {
            setPropertyId(urlPropertyId); // Set propertyId from URL
            const today = new Date();
            const defaultTargetDate = today.toISOString().split('T')[0]; // YYYY-MM-DD
            setTargetDate(defaultTargetDate);

            const fetchPropertyDetails = async () => {
                try {
                    const data = await getProperty(urlPropertyId);
                    setPropertyData(data);
                } catch (err) {
                    setError('Failed to fetch property details.');
                    console.error(err);
                }
            };
            fetchPropertyDetails();
        }
    }, [urlPropertyId]); // Re-run when urlPropertyId changes

    const handleGetTrends = async (e: React.FormEvent) => {
        console.log("handleGetTrends called at: ", new Date().toLocaleTimeString());
        e.preventDefault();
        if (propertyData) { // Ensure propertyData is available
            fetchTrendsData(propertyId, targetDate, propertyData);
        } else {
            setError('Property details not loaded yet.');
        }
    };

    useEffect(() => {
        const loadSavedTrends = async () => {
            if (!propertyId) return;
            setLoading(true);
            try {
                const saved = await getSavedPredictiveTrendsResult(propertyId);
                if (saved) {
                    setSavedTrends(saved);
                    setTrends(saved); // Also set trends to the saved one for display
                }
            } catch (err) {
                console.error('Error loading saved predictive trends:', err);
            } finally {
                setLoading(false);
            }
        };
        loadSavedTrends();
    }, [propertyId]);

    const displayTrends = trends || savedTrends;

    return (
        <div className="container mx-auto p-4">
            <Link to="/properties" className="text-primary hover:underline mb-4 inline-block">{t("predictiveTrends.k1")}</Link>
                        <h1 className="text-2xl font-bold mb-4 flex items-center">
                            {t("predictiveTrends.k2")} <Tooltip text="Predictive Trends analyzes historical data and current market conditions to forecast future property values and market movements. This tool helps users anticipate changes and make informed decisions regarding their real estate investments. Predictive Trends are generated by an AI model and are based on statistical analysis and historical patterns. These predictions are estimates and involve inherent uncertainties. They should not be considered guaranteed outcomes or definitive financial advice. Always conduct your own due diligence and consult with financial experts." />
                        </h1>
            <form onSubmit={handleGetTrends} className="mb-4">
                <div className="flex mb-4">
                    <input
                        type="text"
                        value={propertyId}
                        onChange={(e) => setPropertyId(e.target.value)}
                        className="border border-border-primary p-2 rounded-l flex-grow mr-2"
                        placeholder={t("predictiveTrends.k3")}
                        readOnly={!!urlPropertyId} // Make it read-only if from URL
                        required
                    />
                    <div className="flex flex-col mr-2">
                        <input
                            type="date"
                            id="target_date"
                            value={targetDate}
                            onChange={(e) => setTargetDate(e.target.value)}
                            className="border border-border-primary p-2 w-40"
                            required
                        />
                    </div>
                    <button type="submit" className="bg-primary text-white p-2 rounded-r whitespace-nowrap" disabled={loading}>
                        {loading ? t("predictiveTrends.k4") : t("predictiveTrends.k5")}
                    </button>
                    {displayTrends && !loading && (
                        <button type="button" onClick={handleSaveTrends} className="ml-2 bg-primary text-white p-2 rounded">
                            {t("predictiveTrends.k6")} </button>
                    )}
                </div>
            </form>

            {error && <p className="text-danger">{error}</p>}

            {displayTrends && displayTrends.predicted_value !== null && (
                <div className="border border-border-primary p-4 rounded">
                    <h2 className="text-xl font-bold mb-2">{t("predictiveTrends.k7")} {propertyData?.address}</h2>
                    <p><strong>{t("predictiveTrends.k8")}</strong> {new Date(displayTrends.target_date).toLocaleDateString('en-US')}</p>
                    <div className="mt-4">
                        <p><strong>{t("predictiveTrends.k9")}</strong> {displayTrends.predicted_value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</p>
                        {displayTrends.confidence_score !== null && (
                            <p><strong>{t("predictiveTrends.k10")}</strong> {displayTrends.confidence_score}</p>
                        )}
                        {displayTrends.justification && (
                            <div className="mt-2">
                                <h4 className="text-md font-semibold">{t("predictiveTrends.k11")}</h4>
                                <p>{displayTrends.justification}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
            {displayTrends && displayTrends.predicted_value === null && displayTrends.trend_analysis && displayTrends.trend_analysis.error && (
                <div className="border border-border-primary p-4 rounded mt-4 text-danger">
                    <p><strong>{t("predictiveTrends.k12")}</strong> {displayTrends.trend_analysis.error}</p>
                    <p><strong>{t("predictiveTrends.k13")}</strong> {displayTrends.trend_analysis.raw_response}</p>
                </div>
            )}
        </div>
    );
};

export default PredictiveTrends;
